/**
 * TASK-TGE-009: TronGridEffectsManager - Main Orchestrator
 *
 * Coordinates all Tron-style grid effects including:
 * - Border glow lines with pulse animation
 * - Hex materialization on hover
 * - Creature spawn effects
 * - Phase-based grid visibility
 */

import * as PIXI from 'pixi.js';
import type { AxialCoordinate } from '@drawn-of-war/shared';
import {
  BattlePhase,
  type GridEffectsConfig,
  type HexMaterializationState,
  type SpawnEffectState,
} from '../../types/terrain';
import {
  findBorderHexes,
  getBorderEdges,
  hexToKey,
  parseHexKey,
} from './border-hex-detector';
import { drawGlowingEdge, calculateGlowPulse } from './glowing-line-renderer';
import { renderMaterializationEffect } from './hex-materialization-effect';
import { renderSpawnEffect } from './spawn-effect-renderer';
import { GridFadeAnimator, shouldShowGrid } from './grid-visibility-controller';

/**
 * Deployment zone definition for border detection
 */
export interface DeploymentZone {
  playerId: 'player1' | 'player2';
  hexes: Set<string>;
}

/**
 * Grid bounds for calculating hex positions
 */
export interface GridBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Main manager class for Tron-style grid effects
 */
export class TronGridEffectsManager {
  private config: GridEffectsConfig;
  private container: PIXI.Container;
  private borderLines: PIXI.Graphics;
  private materializationContainer: PIXI.Container;
  private spawnEffectContainer: PIXI.Container;

  private materializationEffects: Map<string, HexMaterializationState> = new Map();
  private spawnEffects: Map<string, SpawnEffectState> = new Map();
  private materializationGraphics: Map<string, PIXI.Graphics> = new Map();
  private spawnGraphics: Map<string, PIXI.Graphics> = new Map();

  private currentPhase: BattlePhase = BattlePhase.DEPLOYMENT;
  private time: number = 0;
  private fadeAnimator: GridFadeAnimator = new GridFadeAnimator();

  // Hex coordinate conversion functions (injected)
  private hexToPixel: (hex: AxialCoordinate) => { x: number; y: number };
  private getHexCorners: (center: { x: number; y: number }) => { x: number; y: number }[];

  /**
   * Create a new TronGridEffectsManager
   *
   * @param config - Grid effects configuration
   * @param hexToPixel - Function to convert hex coordinates to pixel coordinates
   * @param getHexCorners - Function to get the 6 corner points of a hex
   */
  constructor(
    config: GridEffectsConfig,
    hexToPixel: (hex: AxialCoordinate) => { x: number; y: number },
    getHexCorners: (center: { x: number; y: number }) => { x: number; y: number }[]
  ) {
    this.config = config;
    this.hexToPixel = hexToPixel;
    this.getHexCorners = getHexCorners;

    // Create container hierarchy
    this.container = new PIXI.Container();
    this.borderLines = new PIXI.Graphics();
    this.materializationContainer = new PIXI.Container();
    this.spawnEffectContainer = new PIXI.Container();

    // Add layers in order (bottom to top)
    this.container.addChild(this.borderLines);
    this.container.addChild(this.materializationContainer);
    this.container.addChild(this.spawnEffectContainer);
  }

  /**
   * Initialize the effects manager with deployment zones
   *
   * @param deploymentZones - Array of deployment zone definitions
   */
  initialize(deploymentZones: DeploymentZone[]): void {
    this.renderBorderLines(deploymentZones);
  }

  /**
   * Destroy the manager and clean up resources
   */
  destroy(): void {
    this.borderLines.destroy();
    this.materializationContainer.destroy({ children: true });
    this.spawnEffectContainer.destroy({ children: true });
    this.container.destroy({ children: true });

    this.materializationEffects.clear();
    this.spawnEffects.clear();
    this.materializationGraphics.clear();
    this.spawnGraphics.clear();
  }

  /**
   * Set the current battle phase
   * Triggers grid fade animation when transitioning to combat
   *
   * @param phase - New battle phase
   */
  setPhase(phase: BattlePhase): void {
    if (phase === this.currentPhase) return;

    const previousPhase = this.currentPhase;
    this.currentPhase = phase;

    // Start fade animation when transitioning from deployment to combat
    if (previousPhase === BattlePhase.DEPLOYMENT && phase !== BattlePhase.DEPLOYMENT) {
      this.startGridFadeOut();
    }

    // Fade back in if returning to deployment
    if (previousPhase !== BattlePhase.DEPLOYMENT && phase === BattlePhase.DEPLOYMENT) {
      this.fadeAnimator.startFade(1.0, this.config.fadeOutDuration);
    }
  }

  /**
   * Handle hex hover event
   * Triggers materialization effect when hovering over valid hexes
   *
   * @param hex - Hex being hovered, or null if hover ended
   */
  onHexHover(hex: AxialCoordinate | null): void {
    // Only active during deployment phase
    if (this.currentPhase !== BattlePhase.DEPLOYMENT) {
      return;
    }

    if (hex) {
      const hexKey = hexToKey(hex);

      // Start or reactivate materialization effect
      if (this.materializationEffects.has(hexKey)) {
        const effect = this.materializationEffects.get(hexKey)!;
        effect.isActive = true;
      } else {
        this.materializationEffects.set(hexKey, {
          hexKey,
          progress: 0,
          isActive: true,
          startTime: performance.now(),
        });
      }
    } else {
      // Deactivate all effects (they will fade out)
      this.materializationEffects.forEach((effect) => {
        effect.isActive = false;
      });
    }
  }

  /**
   * Handle creature spawn event
   * Triggers spawn effect animation
   *
   * @param unitId - ID of the unit being spawned
   * @param hex - Hex where the unit is spawning
   */
  onCreatureSpawned(unitId: string, hex: AxialCoordinate): void {
    const hexKey = hexToKey(hex);

    this.spawnEffects.set(unitId, {
      unitId,
      hexKey,
      progress: 0,
      startTime: performance.now(),
    });
  }

  /**
   * Update all animations
   * Should be called every frame
   *
   * @param deltaTime - Time elapsed since last frame (milliseconds)
   */
  update(deltaTime: number): void {
    // Update time for pulse animation
    this.time += deltaTime / 1000; // Convert to seconds

    // Update fade animation
    const gridAlpha = this.fadeAnimator.update();

    // Update border glow pulse
    this.updateBorderGlow();

    // Update materialization effects
    this.updateMaterializationEffects(deltaTime);

    // Update spawn effects
    this.updateSpawnEffects(deltaTime);

    // Apply grid fade to materialization container
    this.materializationContainer.alpha = gridAlpha;
  }

  /**
   * Get the container for adding to the scene
   */
  getContainer(): PIXI.Container {
    return this.container;
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /**
   * Render border lines for all deployment zones
   */
  private renderBorderLines(deploymentZones: DeploymentZone[]): void {
    this.borderLines.clear();

    for (const zone of deploymentZones) {
      const borderHexes = findBorderHexes(zone.hexes);

      for (const hexKey of borderHexes) {
        const edges = getBorderEdges(
          hexKey,
          zone.hexes,
          this.hexToPixel,
          this.getHexCorners
        );

        for (const edge of edges) {
          // Draw each border edge with initial glow
          drawGlowingEdge(this.borderLines, edge.start, edge.end, this.config, 1.0);
        }
      }
    }
  }

  /**
   * Update border glow with pulse animation
   */
  private updateBorderGlow(): void {
    const pulseIntensity = calculateGlowPulse(this.time, this.config);
    this.borderLines.alpha = pulseIntensity;
  }

  /**
   * Start grid fade-out animation
   */
  private startGridFadeOut(): void {
    this.fadeAnimator.startFade(0, this.config.fadeOutDuration);
  }

  /**
   * Update materialization effects
   */
  private updateMaterializationEffects(deltaTime: number): void {
    const toRemove: string[] = [];

    for (const [hexKey, state] of this.materializationEffects) {
      if (!state.isActive) {
        // Fade out
        state.progress = Math.max(0, state.progress - deltaTime / this.config.materializeSpeed);
        if (state.progress <= 0) {
          toRemove.push(hexKey);
        }
      } else {
        // Fade in
        state.progress = Math.min(1, state.progress + deltaTime / this.config.materializeSpeed);
      }

      if (state.progress > 0) {
        this.renderMaterialization(hexKey, state.progress);
      }
    }

    // Clean up completed effects
    for (const hexKey of toRemove) {
      this.materializationEffects.delete(hexKey);
      const graphics = this.materializationGraphics.get(hexKey);
      if (graphics) {
        this.materializationContainer.removeChild(graphics);
        graphics.destroy();
        this.materializationGraphics.delete(hexKey);
      }
    }
  }

  /**
   * Update spawn effects
   */
  private updateSpawnEffects(deltaTime: number): void {
    const toRemove: string[] = [];

    for (const [unitId, state] of this.spawnEffects) {
      state.progress += deltaTime / this.config.spawnEffectDuration;

      if (state.progress >= 1) {
        toRemove.push(unitId);
      } else {
        this.renderSpawn(unitId, state);
      }
    }

    // Clean up completed effects
    for (const unitId of toRemove) {
      this.spawnEffects.delete(unitId);
      const graphics = this.spawnGraphics.get(unitId);
      if (graphics) {
        this.spawnEffectContainer.removeChild(graphics);
        graphics.destroy();
        this.spawnGraphics.delete(unitId);
      }
    }
  }

  /**
   * Render a single materialization effect
   */
  private renderMaterialization(hexKey: string, progress: number): void {
    const hex = parseHexKey(hexKey);
    const center = this.hexToPixel(hex);
    const corners = this.getHexCorners(center);

    // Get or create graphics for this hex
    let graphics = this.materializationGraphics.get(hexKey);
    if (!graphics) {
      graphics = new PIXI.Graphics();
      this.materializationContainer.addChild(graphics);
      this.materializationGraphics.set(hexKey, graphics);
    }

    // Render the effect
    renderMaterializationEffect(graphics, center, corners, progress, this.config);
  }

  /**
   * Render a single spawn effect
   */
  private renderSpawn(unitId: string, state: SpawnEffectState): void {
    const hex = parseHexKey(state.hexKey);
    const center = this.hexToPixel(hex);

    // Get or create graphics for this spawn
    let graphics = this.spawnGraphics.get(unitId);
    if (!graphics) {
      graphics = new PIXI.Graphics();
      this.spawnEffectContainer.addChild(graphics);
      this.spawnGraphics.set(unitId, graphics);
    }

    // Render the effect
    renderSpawnEffect(graphics, center, state.progress, this.config);
  }
}
